import { IBranding } from '@/src/types/meetData.type'
import { initialBranding } from '@/src/constants/branding'
import {
  ROOM_LOOKUP_ERROR_CODE,
  RoomLookupError,
  roomLookupErrorFromResponse,
} from '@/src/lib/roomLookupError'

export {
  isOrganizerCodeConnectionFailure,
  isOrganizerCodeRoomNotFound,
  mapOrganizerCodeHttpFailure,
  ROOM_LOOKUP_ERROR_CODE,
  RoomLookupError,
  roomLookupErrorFromResponse,
  getRoomLookupErrorCode,
  roomLookupErrorKindFromCode,
} from '@/src/lib/roomLookupError'

export interface MeetClientRoomData {
  id: number
  name: string
  meet_admin_code: string
  wait_room: boolean
  auto_rec: boolean
  transcript: boolean
  protocol: boolean
  /** Ограничение качества демонстрации; отсутствие сохраняет профиль 1080p. */
  force_lower_share_screen_quality?: boolean
  bitrix_deal_id: string | null
  /**
   * Секция брендирования приходит из таблицы branding_api_keys,
   * которая связана с комнатой через `api_key_id`.
   */
  branding?: IBranding
}

interface MeetClientRoomResponse {
  data?: MeetClientRoomData
  message?: string
  code?: string
}

/**
 * Выполняет HTTP-запрос с повторами при временных сбоях.
 *
 * Этот retry нужен для сетевых колебаний и 5xx от upstream, потому что
 * на pre-call этапе нам важнее получить стабильный ответ, а не упасть после первой ошибки.
 *
 * @param url целевой URL
 * @param options опции fetch
 * @param maxRetries максимальное количество попыток
 * @param delayMs пауза между повторными попытками
 */
async function fetchWithRetry(
  url: string,
  options: RequestInit,
  maxRetries = 3,
  delayMs = 1000,
): Promise<Response | null> {
  for (let attempt = 1; attempt <= maxRetries; attempt += 1) {
    try {
      const response = await fetch(url, options)

      if (response.ok) {
        return response
      }

      if (attempt < maxRetries && response.status >= 500) {
        await new Promise((resolve) => setTimeout(resolve, delayMs))
        continue
      }

      return response
    } catch (error) {
      if (attempt === maxRetries) {
        throw error
      }

      await new Promise((resolve) => setTimeout(resolve, delayMs))
    }
  }

  return null
}

async function readMeetClientRoomResponse(response: Response): Promise<MeetClientRoomResponse> {
  try {
    return (await response.json()) as MeetClientRoomResponse
  } catch {
    return {}
  }
}

/**
 * Запрашивает комнату из meet-client-api по имени.
 *
 * Для Bitrix это основной pre-call источник, который заменяет
 * стандартный путь через Platform `check_meet`. 404 и сбои больше не
 * схлопываются в null — иначе check_access_rights показывает «Неверный код».
 *
 * @param roomName имя комнаты из URL
 */
export async function fetchRoomByName(roomName: string): Promise<MeetClientRoomData> {
  const apiBase = process.env.MEET_CLIENT_API_URL?.replace(/\/$/, '')
  const verifyToken = process.env.MEET_INTERNAL_VERIFY_TOKEN?.trim()

  if (!apiBase || !verifyToken) {
    console.error('MEET_CLIENT_API_URL or MEET_INTERNAL_VERIFY_TOKEN is not configured')
    throw new RoomLookupError(
      'Meet client API is not configured',
      500,
      ROOM_LOOKUP_ERROR_CODE.ROOM_LOOKUP_FAILED,
    )
  }

  const encodedName = encodeURIComponent(roomName.trim())
  const url = `${apiBase}/rooms/by_name/${encodedName}`

  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), 15000)

  try {
    const response = await fetchWithRetry(
      url,
      {
        signal: controller.signal,
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${verifyToken}`,
        },
      },
      3,
      1000,
    )

    clearTimeout(timeoutId)

    if (!response) {
      throw new RoomLookupError(
        'Failed to load room',
        500,
        ROOM_LOOKUP_ERROR_CODE.ROOM_LOOKUP_FAILED,
      )
    }

    const payload = await readMeetClientRoomResponse(response)

    if (!response.ok) {
      throw roomLookupErrorFromResponse(response.status, payload)
    }

    if (!payload.data) {
      throw new RoomLookupError(
        'Failed to load room',
        500,
        ROOM_LOOKUP_ERROR_CODE.ROOM_LOOKUP_FAILED,
      )
    }

    return payload.data
  } catch (error) {
    clearTimeout(timeoutId)

    if (error instanceof RoomLookupError) {
      throw error
    }

    console.error('fetchRoomByName failed:', error)
    throw new RoomLookupError(
      'Failed to load room',
      500,
      ROOM_LOOKUP_ERROR_CODE.ROOM_LOOKUP_FAILED,
    )
  }
}

/**
 * Запрашивает брендинг клиента Битрикс по slug компании из URL Meet.
 *
 * Нужен, когда комнаты нет, а кнопку «На главную» всё равно надо вести на сайт клиента.
 * Не 200 или пустой `url_log_out` на фронте превратятся в `https://proofix.ru`.
 */
export async function fetchBitrixClientBranding(companySlug: string): Promise<IBranding | null> {
  const apiBase = process.env.MEET_CLIENT_API_URL?.replace(/\/$/, '')
  const verifyToken = process.env.MEET_INTERNAL_VERIFY_TOKEN?.trim()
  const normalizedSlug = companySlug.trim()

  if (!apiBase || !verifyToken || !normalizedSlug) {
    console.error('fetchBitrixClientBranding: missing API config or companySlug')

    return null
  }

  const url = `${apiBase}/bitrix-clients/${encodeURIComponent(normalizedSlug)}/branding`

  try {
    const response = await fetch(url, {
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${verifyToken}`,
      },
    })

    if (!response.ok) {
      return null
    }

    const payload = (await response.json()) as { data?: IBranding }

    return payload.data ?? null
  } catch (error) {
    console.error('fetchBitrixClientBranding failed:', error)

    return null
  }
}

/**
 * Преобразует данные комнаты из meet-client-api в формат настроек UI.
 *
 * @param room данные комнаты из meet-client-api
 */
export function mapMeetClientRoomToSettings(room: MeetClientRoomData) {
  return {
    wait_room: Boolean(room.wait_room),
    auto_rec: Boolean(room.auto_rec),
    transcript: room.transcript !== false,
    gpt_protocol: Boolean(room.protocol),
    force_lower_share_screen_quality: room.force_lower_share_screen_quality === true,
    recording_is_avalible: true,
    is_join_sound_enabled: true,
  }
}

/**
 * Преобразует Bitrix-брендинг в формат, который ожидает meet UI.
 *
 * Если в комнате нет кастомного брендирования, мы явно возвращаем fallback
 * на `initialBranding`, чтобы pre-call не ломался на пустых полях темы.
 *
 * @param room данные комнаты из meet-client-api
 */
export function mapMeetClientRoomToBranding(room: MeetClientRoomData): IBranding {
  if (!room.branding) {
    return { ...initialBranding }
  }

  return {
    ...initialBranding,
    ...room.branding,
    border_radius: Number(room.branding.border_radius) || initialBranding.border_radius,
  }
}
